import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';

// Mirrors customerMaster/documentDetails.js's Add/Update/Cancel + grid pattern,
// trimmed to the fields the legacy Agent "Attachment" tab actually has:
// Description, Filename (browse), Doc Date, Expiry Date.
const emptyForm = () => ({ description: '', file: null, filename: '', docDate: null, expDate: null });

const formatDate = (date) => {
  if (!date || date === '0') return '--';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 1900) return '--';
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AgentAttachments = ({ attachmentsList, setAttachmentsList, readOnly = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyForm());
  const [editIndex, setEditIndex] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, file, filename: file?.name || prev.filename }));
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setEditIndex(null);
  };

  const handleAddOrUpdate = () => {
    if (!formData.description?.trim()) return;
    if (editIndex !== null) {
      const updated = [...attachmentsList];
      updated[editIndex] = formData;
      setAttachmentsList(updated);
    } else {
      setAttachmentsList((prev) => [...prev, formData]);
    }
    resetForm();
  };

  const handleEdit = (index) => {
    setFormData({ ...attachmentsList[index], file: null });
    setEditIndex(index);
  };

  // A saved attachment (has a recordId) can't just be dropped from the array -
  // the backend needs the isDeleted:true row to know to remove it. A brand new,
  // never-saved row (no recordId) can simply be spliced out.
  const handleDelete = (index) => {
    const item = attachmentsList[index];
    if (item?.recordId) {
      const updated = [...attachmentsList];
      updated[index] = { ...item, isDeleted: true };
      setAttachmentsList(updated);
    } else {
      setAttachmentsList(attachmentsList.filter((_, i) => i !== index));
    }
    if (editIndex === index) resetForm();
  };

  return (
    <div className="cost-center-section my-3">
      <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
        {!readOnly && (
          <>
            <div className="form-group grid-contain">
              <div className="d-flex">
                <label className="form-label pt-1 start">{t('agentMaster.attachmentFields.description')}</label>
                <input
                  type="text"
                  className="p-2 form-control w-75"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-1 start">{t('agentMaster.attachmentFields.docDate')}</label>
                <DatePicker
                  selected={formData.docDate}
                  onChange={(date) => setFormData((prev) => ({ ...prev, docDate: date }))}
                  dateFormat="dd-MMM-yyyy"
                  className="p-2 form-control w-100"
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-1 start">{t('agentMaster.attachmentFields.filename')}</label>
                <input type="file" className="p-2 form-control w-75" onChange={handleFileChange} />
              </div>
              <div className="d-flex">
                <label className="form-label pt-1 start">{t('agentMaster.attachmentFields.expiryDate')}</label>
                <DatePicker
                  selected={formData.expDate}
                  onChange={(date) => setFormData((prev) => ({ ...prev, expDate: date }))}
                  dateFormat="dd-MMM-yyyy"
                  className="p-2 form-control w-100"
                />
              </div>
            </div>
            <div className="my-3 d-flex gap-2">
              <div className="mainbtnSecondary flex-end gap-2 col-2" onClick={handleAddOrUpdate}>
                <FaPlus color="#fff" className="center mt-1 mx-2" />
                <div className="center">{editIndex !== null ? t('common.update') : t('common.add')}</div>
              </div>
              {editIndex !== null && (
                <div className="mainbtnRed flex-end gap-2 col-2" onClick={resetForm}>
                  <div className="center">{t('common.cancel')}</div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table table-bordered gridshad">
            <thead className="dynoTable">
              <tr>
                <th>{t('agentMaster.attachmentFields.description')}</th>
                <th>{t('agentMaster.attachmentFields.filename')}</th>
                <th>{t('agentMaster.attachmentFields.expiryDate')}</th>
                <th>{t('agentMaster.attachmentFields.docDate')}</th>
                {!readOnly && <th style={{ minWidth: '90px', textAlign: 'center' }}>{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {attachmentsList.map((data, index) => {
                if (data.isDeleted) return null;
                return (
                <tr key={index}>
                  <td>{data.description}</td>
                  <td>{data.filename}</td>
                  <td>{formatDate(data.expDate)}</td>
                  <td>{formatDate(data.docDate)}</td>
                  {!readOnly && (
                    <td className="align-middle text-center" style={{ minWidth: '90px' }}>
                      <div className="d-flex justify-content-center align-items-center gap-3">
                        <FaEdit size={18} onClick={() => handleEdit(index)} style={{ cursor: 'pointer' }} title={t('common.edit')} />
                        <FaTrash size={16} color="red" onClick={() => handleDelete(index)} style={{ cursor: 'pointer' }} title={t('common.delete')} />
                      </div>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgentAttachments;
